import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeft, 
  FileText, 
  Calendar,
  ChevronRight,
  Loader2,
  Download,
  Copy,
  Check,
  Sparkles,
  TrendingUp,
  Users,
  Target,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PerformanceReviewPage = () => {
  const { isAuthenticated, loading: authLoading, token } = useAuth();
  const navigate = useNavigate();
  
  const [reviews, setReviews] = useState([]);
  const [quarters, setQuarters] = useState({ current: null, available: [] });
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [currentReview, setCurrentReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load existing reviews and available quarters
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [reviewsRes, quartersRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/reviews`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/dashboard/reviews/quarters`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData.reviews || []);
        }

        if (quartersRes.ok) {
          const quartersData = await quartersRes.json();
          setQuarters(quartersData);
          setSelectedQuarter(quartersData.current?.label);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && token) {
      loadData();
    }
  }, [isAuthenticated, token]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/dashboard/reviews/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          period_type: 'quarter',
          period_label: selectedQuarter
        })
      });

      if (!res.ok) {
        throw new Error('Failed to generate review');
      }

      const review = await res.json();
      setCurrentReview(review);
      
      // Refresh reviews list
      const reviewsRes = await fetch(`${API_URL}/api/dashboard/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData.reviews || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleExport = async () => {
    if (!currentReview?.id) return;
    
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/dashboard/reviews/${currentReview.id}/export?format=text`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        // Copy to clipboard
        try {
          await navigator.clipboard.writeText(data.content);
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        } catch (clipboardErr) {
          // Fallback for browsers that don't support clipboard API
          console.error('Clipboard API failed:', clipboardErr);
          setError('Failed to copy to clipboard');
        }
      } else {
        setError('Failed to export review');
      }
    } catch (err) {
      setError('Failed to export review');
    } finally {
      setExporting(false);
    }
  };

  const handleLoadReview = async (reviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/dashboard/reviews/${reviewId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const review = await res.json();
        setCurrentReview(review);
        setSelectedQuarter(review.period_label);
      }
    } catch (err) {
      setError('Failed to load review');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  const summary = currentReview?.summary || {};

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="text-zinc-400 hover:text-white transition-colors"
              data-testid="review-back-link"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-white">Performance Review</h1>
              <p className="text-sm text-zinc-400">Generate your quarterly summary</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Generation Controls */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quarter Selector */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                Select Quarter
              </h3>
              
              <select
                value={selectedQuarter || ''}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                data-testid="quarter-selector"
              >
                {quarters.available?.map((q) => (
                  <option key={q.label} value={q.label}>
                    {q.label} {q.label === quarters.current?.label ? '(Current)' : ''}
                  </option>
                ))}
              </select>
              
              <Button
                onClick={handleGenerate}
                disabled={generating || !selectedQuarter}
                className="w-full mt-4 bg-red-600 hover:bg-red-700 text-white"
                data-testid="generate-review-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Review
                  </>
                )}
              </Button>
            </div>

            {/* Previous Reviews */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-white font-medium mb-4">Previous Reviews</h3>
              
              {loading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                </div>
              ) : reviews.length === 0 ? (
                <p className="text-zinc-500 text-sm">No reviews generated yet</p>
              ) : (
                <div className="space-y-2">
                  {reviews.map((review) => (
                    <button
                      key={review.id || review.period_label}
                      onClick={() => handleLoadReview(review.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                        currentReview?.period_label === review.period_label
                          ? 'bg-red-500/20 border border-red-500/30'
                          : 'bg-zinc-800 hover:bg-zinc-700'
                      }`}
                      data-testid={`review-${review.period_label}`}
                    >
                      <p className="text-white text-sm font-medium">{review.period_label}</p>
                      <p className="text-zinc-400 text-xs truncate">
                        {review.summary?.headline || 'Review generated'}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Review Display */}
          <div className="lg:col-span-2">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {!currentReview ? (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center">
                <FileText className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">
                  Generate Your Review
                </h3>
                <p className="text-zinc-400 max-w-md mx-auto mb-6">
                  Select a quarter and generate your performance review. We'll compile your achievements, skills demonstrated, and development areas from your reflections.
                </p>
                <Badge className="bg-zinc-800 text-zinc-300">
                  Uses your Living CV, Accomplishments & Situations data
                </Badge>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Review Header */}
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <Badge className="mb-2 bg-red-500/20 text-red-400 border-red-500/30">
                        {currentReview.period_label}
                      </Badge>
                      <h2 className="text-2xl font-bold text-white">
                        {summary.headline || 'Performance Review'}
                      </h2>
                    </div>
                    <Button
                      onClick={handleExport}
                      disabled={exporting}
                      variant="outline"
                      className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                      data-testid="export-review-btn"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 mr-2 text-green-500" />
                          Copied!
                        </>
                      ) : exporting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy to Clipboard
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <p className="text-zinc-300 leading-relaxed">
                    {summary.narrative}
                  </p>
                  
                  {/* Stats Row */}
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">
                        {currentReview.source_data?.reflection_count || 0}
                      </p>
                      <p className="text-xs text-zinc-400">Reflections</p>
                    </div>
                    <div className="bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">
                        {currentReview.source_data?.accomplishment_count || 0}
                      </p>
                      <p className="text-xs text-zinc-400">Accomplishments</p>
                    </div>
                    <div className="bg-zinc-800 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">
                        {summary.situations_handled || 0}
                      </p>
                      <p className="text-xs text-zinc-400">Situations</p>
                    </div>
                  </div>
                </div>

                {/* Key Achievements */}
                {summary.key_achievements?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-green-500" />
                      Key Achievements
                    </h3>
                    <ul className="space-y-3">
                      {summary.key_achievements.map((achievement, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-300">{achievement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Skills Demonstrated */}
                {summary.skills_demonstrated?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-500" />
                      Skills Demonstrated
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {summary.skills_demonstrated.map((skill, idx) => (
                        <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{skill.skill}</span>
                            <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                              {skill.evidence_count} instances
                            </Badge>
                          </div>
                          {skill.examples?.length > 0 && (
                            <ul className="text-sm text-zinc-400 space-y-1">
                              {skill.examples.map((ex, i) => (
                                <li key={i} className="truncate">• {ex}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Development Areas */}
                {summary.development_areas?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-amber-500" />
                      Development Areas
                    </h3>
                    <div className="space-y-3">
                      {summary.development_areas.map((area, idx) => (
                        <div key={idx} className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                          <p className="text-amber-200 font-medium">{area.area}</p>
                          <p className="text-amber-400/80 text-sm mt-1">{area.context}</p>
                          {area.suggested_focus && (
                            <p className="text-zinc-400 text-sm mt-2 italic">{area.suggested_focus}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collaborations */}
                {summary.people_collaborated?.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-purple-500" />
                      Collaborations
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {summary.people_collaborated.map((person, idx) => (
                        <Badge key={idx} className="bg-purple-500/20 text-purple-300 border-purple-500/30">
                          {person}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PerformanceReviewPage;
